import { assign, defaults, difference, filter, find, findIndex, forEach, functions, isArray, isObjectLike, keys as ldKeys, map } from 'lodash';
import validator from './validator';

export enum PatchOpType {
	add = 'add',
	remove = 'remove',
	replace = 'replace',
}

export interface IPatchOperation {
	op: PatchOpType;
	path: string;
	value?: any;
}

export interface IJSONPathTarget {
	target: any;
	property: string;
}

export interface IShapeContext {
	source?: object;
	target?: object;
	path?: string;
	property?: string;
	value?: any;
	jsonPatch?: IPatchOperation[];
}

export type ShapeFilter = (c: object) => boolean;

export interface IModel {
	key?: string;
	createdById?: string;
	dateCreated?: Date;
	lastModifiedById?: string;
	dateLastModified?: Date;
	dateModifiedOrCreated?: Date;
	itemVersion?: number;
	relayNumber?: string;
	relayId?: string;
	schemaVersion?: number;
	clone: (context?: any) => any;
	copy: () => any;
	getModelName: () => string;
	getPropertyNames: (includeNonSerialized?: boolean) => string[];
	toJSON: () => Record<string, any>;
	getAt: (jsonPointer: string) => any;
	setAt: (jsonPointer: string, val: any) => void;
	viewValue: (jsonPointer: string) => any;
	resetBackingValue: (name: string) => void;
	getPathTarget: (jsonPointer: string) => IJSONPathTarget;
	getPatch: (target: object, keys?: string[], context?: IShapeContext) => IPatchOperation[];
	toDocument: (keys?: string[], filter?: string[] | ShapeFilter, context?: IShapeContext) => Record<string, any>;
	fromDocument: (document: object, keys?: string[], filter?: string[] | ShapeFilter) => IModel;
	test: (property?: string, messages?: string[]) => string[];
	validate: (property?: string) => void;
	isValid: (property?: string) => boolean;
	applyPatch: (jsonPatch: IPatchOperation[]) => void;
}

function _createAccessorProperty(descriptor) {
	const name = descriptor.name;
	const defaultVal = descriptor.defaultValue;
	let setFilters = descriptor.setFilters || [];
	let getFilters = descriptor.getFilters || [];

	setFilters = isArray(setFilters) ? setFilters : [setFilters];
	getFilters = isArray(getFilters) ? getFilters : [getFilters];

	const setter = function (val) {
		let newVal = val;

		setFilters.forEach(x => {
			newVal = x.bind(this)(newVal);
		});

		if (typeof this['_set_' + name] === 'function') {
			this['_set_' + name](newVal);
		}
		else {
			const innerVal = this.__getBackingValue(name);

			innerVal.hasBeenSet = (typeof newVal !== 'undefined');
			innerVal.value = newVal;
		}
	};

	const getter = function () {
		let result;

		if (typeof this['_get_' + name] === 'function') {
			result = this['_get_' + name]();
		}
		else {
			const innerVal = this.__getBackingValue(name);

			if (!innerVal.hasBeenSet && typeof defaultVal !== 'undefined') {
				setter.bind(this)((typeof defaultVal === 'function') ? defaultVal.bind(this)() : defaultVal);
			}
			result = innerVal.value;
		}

		getFilters.forEach(x => {
			result = x.bind(this)(result);
		});

		return result;
	};

	const result = {
		name: name,
		configurable: descriptor.configurable,
		enumerable: descriptor.enumerable,
		get: getter
	};

	if (descriptor.writable) {
		(result as any).set = setter;
		if (typeof result.enumerable === 'undefined') {
			result.enumerable = true;
		}
	}
	else {
		(result as any).privateSet = setter;
	}

	return result;
}

function _addProperties(object, properties) {
	properties.forEach(function (x) {
		const descriptor = x;

		descriptor.enumerable = true;
		Object.defineProperty(object, descriptor.name, descriptor);

		// add a private setter
		if (!descriptor.writable && typeof descriptor.privateSet === 'function') {
			Object.defineProperty(object, '_' + descriptor.name, { enumerable: false, get: descriptor.get, set: descriptor.privateSet });
		}
	});
}

function _shape(source, target, keys, filter, context?) {
	if (typeof source === 'undefined' || source === null) { return source; }

	const path = (context) ? context.path : '';
	const filterer = (typeof filter === 'function') ? filter : function filterWrapper(ctx) { return filter ? filter.indexOf(ctx.path) === -1 : true; };
	const ctx = context || { source: source, target: target };

	// use the model keys
	keys = keys || ldKeys(source);

	keys.forEach(function (x) {
		const val = source[x];

		ctx.path = path ? path + '.' + x : x;
		ctx.property = x;
		ctx.value = val;
		if (filterer(ctx)) {
			if (typeof val === 'function' || typeof val === 'undefined' || typeof target[x] === 'function' || val === null) {
				return;
			}
			else if (typeof val.toDocument === 'function') {
				target[x] = val.toDocument(null, filterer, ctx);
			}
			else if (typeof val.toDocumentArray === 'function') {
				target[x] = val.toDocumentArray(null, filterer, ctx);
			}
			else {
				target[x] = val;
			}
		}
	});
	return target;
}

function _clone(source, target, keys, filterer, context) {
	if (typeof source === 'undefined' || source === null) { return source; }

	const ctx = assign({ path: '' }, context, { source: source, target: target });

	keys.forEach(function (x) {
		const val = source[x];

		ctx.path = ctx.path ? ctx.path + '.' + x : x;
		ctx.property = x;
		ctx.value = val;
		if (filterer(ctx)) {
			if (typeof val === 'function' || typeof val === 'undefined' || typeof target[x] === 'function' || val === null) {
				return;
			}
			else if (typeof val.clone === 'function') {
				target[x] = val.clone(ctx);
			}
			else {
				target[x] = val;
			}
		}
	});

	return target;
}

// NOTE: returns JsonPatch, DOES NOT honor array ordering for added items
function _createPatch(source, target, keys, context) {
	if (typeof source === 'undefined' || source === null) { return context; }

	const path = (context) ? context.path : '';
	const ctx = context || { jsonPatch: [], source: source, target: target };

	// use the model keys
	keys = keys || ldKeys(source);

	forEach(keys, x => {
		const val = source[x];
		const targetVal = target[x];

		ctx.path = path + '/' + x;

		if (val && typeof val.toDocument === 'function') {
			val.getPatch(targetVal, null, ctx);
		}
		else if (isArray(val)) {
			const basePath = ctx.path;
			const valKeys = map(val, v => (v.key || v));
			const targetKeys = map(targetVal, v => (v.key || v));

			forEach(difference(targetKeys, valKeys), k => { // removed
				const itemIndex = findIndex(targetVal, (v: any) => (v === k || v.key === k));

				ctx.jsonPatch.push({ op: 'remove', path: basePath + '/' + itemIndex });
			});

			forEach(difference(valKeys, targetKeys), k => { // added
				ctx.jsonPatch.push({ op: 'add', path: basePath + '/-', value: find(val, v => (v === k || v.key === k)) });
			});

			forEach(valKeys, k => {
				const itemIndex = findIndex(val, v => (v === k || v.key === k));
				const itemVal = val[itemIndex];
				const itemTargetVal = find(targetVal, v => (v === k || v.key === k));

				if (itemTargetVal) {
					const itemPath = basePath + '/' + itemIndex;
					if (typeof itemVal.getPatch === 'function') {
						ctx.path = itemPath;
						itemVal.getPatch(itemTargetVal, null, ctx);
					}
					else if (itemVal !== itemTargetVal) {
						ctx.jsonPatch.push({ op: (typeof targetVal === 'undefined') ? 'add' : 'replace', path: itemPath, value: val });
					}
				}
			});
		}
		else if (val && typeof val.toDocumentArray === 'function') {
			if (targetVal) {
				const basePath = ctx.path;
				const valKeys = ldKeys(val);
				const targetKeys = ldKeys(targetVal);
				const added = difference(valKeys, targetKeys);

				forEach(difference(targetKeys, valKeys), k => { // removed
					ctx.jsonPatch.push({ op: 'remove', path: basePath + '/' + k });
				});

				forEach(added, k => { // added
					ctx.jsonPatch.push({ op: 'add', path: basePath + '/' + k, value: val[k].toDocument() });
				});

				forEach(difference(valKeys, added), k => {
					ctx.path = basePath + '/' + k;
					val[k].getPatch(targetVal[k], null, ctx);
				});
			}
			else {
				ctx.jsonPatch.push({ op: 'remove', path: ctx.path });
			}
		}
		else if (typeof val !== 'function' && typeof targetVal !== 'function' && val !== targetVal) {
			if (typeof val === 'undefined') {
				ctx.jsonPatch.push({ op: 'remove', path: ctx.path });
			}
			else if (typeof val.getTime === 'function' && typeof targetVal !== 'undefined') {
				if (val.getTime() !== targetVal.getTime()) {
					ctx.jsonPatch.push({ op: (typeof targetVal === 'undefined') ? 'add' : 'replace', path: ctx.path, value: val });
				}
			}
			else {
				ctx.jsonPatch.push({ op: (typeof targetVal === 'undefined') ? 'add' : 'replace', path: ctx.path, value: val });
			}
		}
	});

	return ctx;
}

interface FactoryFunction<TResult> {
	(document?): TResult;
	rules(property?: string): any;
	setFilters(property?: string): any;
	sanitizePatch(any): any;
}

const _factories = {};
const modelFactory = {
	getFactory: function (name) {
		return _factories[name];
	},

	createFactory<TModel extends object>(name: string, def?: Record<string, any>, modelRules?): FactoryFunction<TModel> {
		// eslint-disable-next-line prefer-const
		let factory: FactoryFunction<TModel>; // make this available to Model prototype methods;
		const rules: Record<string, any[]> = {};
		const setFilters: Record<string, any[]> = {};
		const propertyNames: any[] = [];
		const serializedPropertyNames: any[] = [];
		const properties = map(filter(ldKeys(def), function (x) { return typeof def[x] !== 'function'; }), function (x) {
			const prop = def[x];
			const property = _createAccessorProperty(defaults(prop || {}, { name: x, enumerable: true, writable: true }));

			(property as any).nonSerialized = prop.nonSerialized;

			if (prop.rules) {
				rules[property.name] = rules[property.name] || [];
				rules[property.name] = rules[property.name].concat(prop.rules);
			}

			if (prop.setFilters) {
				setFilters[property.name] = setFilters[property.name] || [];
				setFilters[property.name] = setFilters[property.name].concat(prop.setFilters);
			}

			propertyNames.push(property.name);
			if (!prop.nonSerialized) {
				serializedPropertyNames.push(property.name);
			}
			return property;
		});

		if (modelRules) { // these are rules applied to the model as a whole rather than an individual property
			(rules as any)._model_ = isArray(modelRules) ? modelRules : [modelRules];
		}

		const _getRules = (property?) => {
			if (property) {
				return rules[property] || [];
			}
			return rules;
		};

		const _getSetFilters = (property) => {
			if (property) {
				return setFilters[property] || [];
			}
			return setFilters;
		};

		// constructor
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		function Model() { }

		// interface
		Model.prototype = {
			getModelName: function () {
				return name;
			},
			getPropertyNames: function (includeNonSerialized = false) {
				return (includeNonSerialized) ? propertyNames : serializedPropertyNames;
			},
			toJSON: function () {
				return this.toDocument();
			},
			getAt: function (jsonPointer) {
				const t = this.getPathTarget(jsonPointer);

				return t.target[t.property];
			},
			setAt: function (jsonPointer, val) {
				const t = this.getPathTarget(jsonPointer);

				if (typeof val !== 'undefined') {
					if (typeof t.target.toDocumentArray === 'function' && /^[\d-]+$/.test(t.property)) {
						if (t.property === '-') {
							t.target.append(val);
						}
						else {
							t.target.setAt(t.property, val);
						}
					}
					else {
						t.target[t.property] = val;
					}
				}
				else if (typeof t.target.toDocumentArray === 'function') {
					t.target.splice(t.property, 1);
				}
				else {
					t.target[t.property] = undefined;
				}
			},
			viewValue: function (jsonPointer) {
				const t = this.getPathTarget(jsonPointer);
				const val = t.target[t.property];
				return (typeof val === 'undefined' || val === null) ? '' : val;
			},
			getPathTarget: function (jsonPointer) {
				jsonPointer = (jsonPointer[0] !== '/') ? '/' + jsonPointer : jsonPointer;
				let getFilters = _getSetFilters;
				let f, filters;
				const segments = jsonPointer.split('/').slice(1); // remove the empty segment at the root
				let segment = segments.shift();
				const result = { target: this, property: segment };

				while (segment) {
					filters = getFilters(segment);
					if (filters && filters.length && typeof filters[0].factory === 'function') {
						f = filters[0].factory;
						getFilters = f.setFilters;
						if (filters[0]._isModelArray) {
							result.target[segment] = result.target[segment] || [];
							if (segments.length) {
								result.target = result.target[segment];
								segment = segments.shift();
								result.property = segment;
							}
						}
						else if (segments.length) {
							result.target[segment] = result.target[segment] || f();
							result.target = result.target[segment];
						}
					}
					else if ((isObjectLike(result.target[segment]) || isArray(result.target[segment])) && segments.length) {
						result.target = result.target[segment];
					}

					segment = segments.shift();
					result.property = segment || result.property;
				}

				return result;
			},
			_getPatch: function (target, keys, context) {
				if (this === target) { return []; }

				const patch = _createPatch(this, target, keys || this.getPropertyNames(), context).jsonPatch;

				return patch;
			},
			getPatch: function (target, keys, context) {
				return this._getPatch(target, keys, context);
			},
			toDocument: function (keys, filter, context) {
				return this._toDocument(keys, filter, context);
			},
			_toDocument: function (keys, filter, context) {
				const result = {};

				_shape(this, result, keys || this.getPropertyNames(), filter, context);
				return result;
			},
			clone: function (ctx) {
				return this._clone(ctx);
			},
			copy: function () {
				return this._clone(undefined, () => true);
			},
			_clone: function (context, filterer) {
				const baseFilter = function (ctx) { return ['key', 'rootKey', 'changeOrderKey', 'itemVersion', 'createdById', 'dateCreated', 'lastModifiedById', 'dateLastModified', 'schemaVersion'].indexOf(ctx.property) === -1; };
				const filter = (filterer) ? function (ctx) {
					return baseFilter(ctx) && filterer(ctx);
				} : baseFilter;

				return _clone(this, factory({}), this.getPropertyNames(), filter, context);
			},
			_fromDocument: function (document, keys, filter) {
				const filterer = (typeof filter === 'function') ? filter : function filterWrapper(ctx) { return filter ? filter.indexOf(ctx.path) !== 0 : true; };

				return _shape(document, this, keys || this.getPropertyNames(), function (ctx) {
					const val = ctx.value;

					if (filterer(ctx) && typeof val !== 'function' && typeof val !== 'undefined') {
						let x = ctx.property;
						let descriptor = Object.getOwnPropertyDescriptor(Model.prototype, x);

						if (!descriptor || (!descriptor.writable && !descriptor.set)) {
							x = '_' + x;
							descriptor = Object.getOwnPropertyDescriptor(Model.prototype, x);
						}

						if (descriptor && ctx.target) {
							ctx.target[x] = val;
						}
					}
					return false;
				});
			},
			fromDocument: function (document, keys, filter) {
				return this._fromDocument(document, keys, filter);
			},
			addValidationRule: function (...args) { // optional key, function
				if (args.length === 1) {
					// this is a model level
				}
				else {
					// this is a propertyLevel
				}
			},
			_validate: function (property) {
				if (!property) {
					validator.validate(this, _getRules(), 'Invalid ' + name);
				}
				else {
					validator.validateProperty(this, property, _getRules(property));
				}
			},
			validate: function (property) {
				this._validate(property);
			},
			test: function (property, messages) {
				if (!property) {
					return validator.test(this, _getRules(), messages);
				}
				else {
					return validator.testProperty(this, property, _getRules(property), messages);
				}
			},
			isValid: function (property) {
				// returns undefined as valid result
				return !this.test(property);
			},
			applyPatch: function (jsonPatch) {
				const messages = [];

				forEach(jsonPatch, (op) => {
					const t = this.getPathTarget(op.path);

					if (op.op === 'add' || op.op === 'replace') {
						if (typeof t.target.toDocumentArray === 'function') {
							op.value = t.target.add(op.value);
						}
						else if (isArray(t.target)) {
							const idx = (op.op === 'add') ? t.target.length : Number(t.property);
							t.target.splice(idx, 0, op.value);
						}
						else {
							t.target[t.property] = op.value;
							op.value = t.target[t.property];
						}
					}
					else if (op.op === 'remove') {
						if (typeof t.target.toDocumentArray === 'function') {
							t.target.delete(t.property);
						}
						else if (isArray(t.target)) {
							t.target.splice(Number(t.property), 1);
						}
						else {
							t.target[t.property] = undefined;
							op.value = t.target[t.property];
						}
					}
				});

				return this.test(undefined, messages);
			}
		};

		// add functions/overrides
		forEach(functions(def), function (x) {
			Model.prototype[x] = def[x];
		});

		// add properties
		_addProperties(Model.prototype, properties);

		factory = Object.assign(function ModelFactory(doc) {
			const backingValues = {};
			const model = new Model();

			Object.defineProperty(model, '__getBackingValue', {
				value: (property, initialValue) => {
					backingValues[property] = backingValues[property] || { hasBeenSet: !!initialValue, value: (typeof initialValue === 'function') ? initialValue() : initialValue };
					return backingValues[property];
				}
			});

			model.fromDocument(doc || {});

			return model;
		}, {
			rules: _getRules,
			setFilters: _getSetFilters,
			sanitizePatch: (jsonPatch) => {
				const instance = factory() as unknown as any;
				return instance.applyPatch ? instance.applyPatch(jsonPatch) : [];
			}
		});

		// factory.rules = _getRules;
		// factory.setFilters = _getSetFilters;
		// factory.sanitizePatch = (jsonPatch) => {
		// 	return factory().applyPatch(jsonPatch);
		// };

		_factories[name] = factory;

		return factory;
	}

};

export default modelFactory;